import React, { useState } from 'react';
import { 
  Box, Card, CardContent, Grid, Typography, Chip, Button, 
  LinearProgress, Avatar, AvatarGroup, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField
} from '@mui/material';
import { 
  Add, EmojiEvents, TrendingUp, Group, CalendarMonth,
  FitnessCenter, DirectionsRun, Pool, SelfImprovement
} from '@mui/icons-material';
import { useThemeMode } from '../context/ThemeContext';

// Challenges activos para Caldea
const activeChallenges = [
  {
    id: 1,
    name: 'Semana Wellness',
    description: 'Completa 4 sesiones de entrenamiento esta semana',
    type: 'WEEKLY',
    category: 'FITNESS',
    icon: FitnessCenter,
    reward: 'Upgrade Circuito Recovery',
    participants: 156,
    completions: 42,
    startDate: '2026-01-20',
    endDate: '2026-01-27',
    progress: 75,
    status: 'ACTIVE'
  },
  {
    id: 2,
    name: 'Runner Caldea',
    description: 'Acumula 15km corriendo en el mes de enero',
    type: 'MONTHLY',
    category: 'CARDIO',
    icon: DirectionsRun,
    reward: 'Pack Recovery Premium',
    participants: 89,
    completions: 12,
    startDate: '2026-01-01',
    endDate: '2026-01-31',
    progress: 45,
    status: 'ACTIVE'
  },
  {
    id: 3,
    name: 'Balance Mental',
    description: 'Completa 5 sesiones de breathwork o mindfulness',
    type: 'MONTHLY',
    category: 'WELLNESS',
    icon: SelfImprovement,
    reward: 'Quiet Hour VIP',
    participants: 67,
    completions: 23,
    startDate: '2026-01-01',
    endDate: '2026-01-31',
    progress: 60,
    status: 'ACTIVE'
  },
  {
    id: 4,
    name: 'Circuito Completo',
    description: 'Usa todas las zonas: gym, spa, relax en una semana',
    type: 'WEEKLY',
    category: 'EXPERIENCE',
    icon: Pool,
    reward: 'Hidratación Premium + Descarga',
    participants: 112,
    completions: 34,
    startDate: '2026-01-20',
    endDate: '2026-01-27',
    progress: 82,
    status: 'ACTIVE'
  }
];

// Top performers
const leaderboard = [
  { rank: 1, name: 'Maria García', improvementScore: 245, streak: 12, avatar: '👩' },
  { rank: 2, name: 'Carlos Pérez', improvementScore: 218, streak: 10, avatar: '👨' },
  { rank: 3, name: 'Ana López', improvementScore: 195, streak: 8, avatar: '👩‍🦰' },
  { rank: 4, name: 'Pedro Martín', improvementScore: 182, streak: 7, avatar: '🧔' },
  { rank: 5, name: 'Laura Sánchez', improvementScore: 165, streak: 6, avatar: '👱‍♀️' },
];

export default function ChallengesPage() {
  const [openDialog, setOpenDialog] = useState(false);
  const { isDark, colors } = useThemeMode();
  
  // Theme-aware colors
  const themeColors = {
    cardBg: isDark ? '#1e293b' : '#ffffff',
    cardBorder: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
    textPrimary: isDark ? '#f1f5f9' : '#1e293b',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    chipBg: isDark ? 'rgba(29,202,255,0.2)' : 'rgba(29,202,255,0.1)',
    tableBg: isDark ? '#0f172a' : '#f8fafc',
  };

  const categoryColors = {
    FITNESS: colors.primary,
    CARDIO: colors.error,
    WELLNESS: '#a855f7',
    EXPERIENCE: '#14b8a6'
  };

  const getChallengeCard = (challenge) => {
    const Icon = challenge.icon;
    return (
      <Card 
        key={challenge.id} 
        sx={{ 
          height: '100%', 
          position: 'relative', 
          overflow: 'visible',
          borderRadius: 3,
          border: `1px solid ${themeColors.cardBorder}`,
          boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.2)' : '0 1px 3px rgba(0,0,0,0.04)',
          transition: 'all 0.2s ease',
          bgcolor: themeColors.cardBg,
          '&:hover': { boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.4)' : '0 8px 24px rgba(0,0,0,0.08)', transform: 'translateY(-2px)' }
        }}
      >
        {challenge.progress >= 75 && (
          <Chip 
            label="🔥 Hot" 
            size="small" 
            sx={{ 
              position: 'absolute', 
              top: -10, 
              right: 10, 
              background: `linear-gradient(135deg, ${colors.warning} 0%, #ea580c 100%)`,
              color: 'white',
              fontWeight: 600,
              fontSize: '0.7rem',
              boxShadow: '0 2px 8px rgba(249,115,22,0.4)'
            }} 
          />
        )}
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
            <Box sx={{ 
              background: isDark ? `${categoryColors[challenge.category]}30` : `linear-gradient(135deg, ${categoryColors[challenge.category]}15 0%, ${categoryColors[challenge.category]}08 100%)`,
              p: 1.5, 
              borderRadius: 2.5
            }}>
              <Icon sx={{ color: categoryColors[challenge.category], fontSize: 24 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.95rem', color: themeColors.textPrimary, lineHeight: 1.3 }}>
                {challenge.name}
              </Typography>
              <Chip 
                label={challenge.type} 
                size="small" 
                sx={{ 
                  backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                  color: themeColors.textSecondary,
                  fontSize: '0.65rem',
                  height: 20,
                  mt: 0.5
                }} 
              />
            </Box>
          </Box>
          
          <Typography variant="body2" sx={{ color: themeColors.textSecondary, mb: 2, minHeight: 40, fontSize: '0.8rem', lineHeight: 1.5 }}>
            {challenge.description}
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: themeColors.textSecondary, fontSize: '0.7rem' }}>Progreso global</Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, color: themeColors.textPrimary, fontSize: '0.7rem' }}>{challenge.progress}%</Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={challenge.progress} 
              sx={{ 
                height: 6, 
                borderRadius: 3,
                backgroundColor: 'rgba(0,0,0,0.06)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: categoryColors[challenge.category],
                  borderRadius: 3
                }
              }} 
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Group fontSize="small" sx={{ color: '#94a3b8', fontSize: 16 }} />
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#64748b' }}>{challenge.participants} participantes</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <EmojiEvents fontSize="small" sx={{ color: '#eab308', fontSize: 16 }} />
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#64748b' }}>{challenge.completions} completados</Typography>
            </Box>
          </Box>

          <Box sx={{ 
            background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
            p: 1.5, 
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#15803d', fontSize: '0.8rem' }}>
              🎁 {challenge.reward}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.75rem', mb: 0.5 }}>
            Wellness Challenges
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Caldea Club × PEpL — Desafíos de Bienestar
          </Typography>
        </Box>
        <Button 
          variant="contained" 
          startIcon={<Add />} 
          onClick={() => setOpenDialog(true)}
          sx={{ 
            background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
            boxShadow: '0 4px 14px rgba(14,165,233,0.35)',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3
          }}
        >
          Nuevo Challenge
        </Button>
      </Box>

      {/* KPIs Summary */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            borderRadius: 3, 
            border: '1px solid rgba(0,0,0,0.06)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            transition: 'all 0.2s ease',
            '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,0.08)', transform: 'translateY(-2px)' }
          }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: 'rgba(14,165,233,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <EmojiEvents sx={{ color: '#0284c7', fontSize: 20 }} />
                </Box>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1.75rem' }}>4</Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem', mb: 0.5 }}>Challenges Activos</Typography>
              <Typography variant="caption" sx={{ color: '#22c55e', fontWeight: 500 }}>2 semanales + 2 mensuales</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            borderRadius: 3, 
            border: '1px solid rgba(0,0,0,0.06)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            transition: 'all 0.2s ease',
            '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,0.08)', transform: 'translateY(-2px)' }
          }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: 'rgba(34,197,94,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Group sx={{ color: '#16a34a', fontSize: 20 }} />
                </Box>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1.75rem' }}>424</Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem', mb: 0.5 }}>Participantes Totales</Typography>
              <Typography variant="caption" sx={{ color: '#22c55e', fontWeight: 500 }}>↑ 23% vs mes anterior</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            borderRadius: 3, 
            border: '1px solid rgba(0,0,0,0.06)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            transition: 'all 0.2s ease',
            '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,0.08)', transform: 'translateY(-2px)' }
          }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: 'rgba(234,179,8,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <TrendingUp sx={{ color: '#ca8a04', fontSize: 20 }} />
                </Box>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1.75rem' }}>111</Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem', mb: 0.5 }}>Completados Este Mes</Typography>
              <Typography variant="caption" sx={{ color: '#94a3b8' }}>26% tasa de éxito</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            borderRadius: 3, 
            border: '1px solid rgba(0,0,0,0.06)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            transition: 'all 0.2s ease',
            '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,0.08)', transform: 'translateY(-2px)' }
          }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: 'rgba(249,115,22,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FitnessCenter sx={{ color: '#ea580c', fontSize: 20 }} />
                </Box>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1.75rem' }}>3.2</Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem', mb: 0.5 }}>Frecuencia Media</Typography>
              <Typography variant="caption" color="success.main">visitas/semana</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Challenges Grid */}
        <Grid item xs={12} md={8}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Challenges Activos
          </Typography>
          <Grid container spacing={2}>
            {activeChallenges.map((challenge) => (
              <Grid item xs={12} sm={6} key={challenge.id}>
                {getChallengeCard(challenge)}
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Leaderboard */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                🏅 Top 5 del Mes
              </Typography>
              {leaderboard.map((user) => (
                <Box 
                  key={user.rank} 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2, 
                    py: 1.5,
                    borderBottom: '1px solid #f0f0f0'
                  }}
                >
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      width: 30, 
                      fontWeight: 700,
                      color: user.rank <= 3 ? '#FFD700' : '#999'
                    }}
                  >
                    #{user.rank}
                  </Typography>
                  <Avatar sx={{ width: 36, height: 36, fontSize: 20 }}>
                    {user.avatar}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{user.name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      🔥 {user.streak} días seguidos
                    </Typography>
                  </Box>
                  <Chip 
                    label={`Nivel ${user.improvementScore}`} 
                    size="small" 
                    sx={{ fontWeight: 600, backgroundColor: '#E3F2FD' }}
                  />
                </Box>
              ))}

              <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  📊 Retención por Challenges
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Usuarios en challenges tienen <strong>47% más retención</strong> a 30 días que usuarios sin participación.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog Nuevo Challenge */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuevo Wellness Challenge</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Nombre del Challenge" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Descripción" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Tipo" select defaultValue="WEEKLY">
                <option value="WEEKLY">Semanal</option>
                <option value="MONTHLY">Mensual</option>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Recompensa" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" sx={{ backgroundColor: '#00A9CE' }}>Crear Challenge</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
